import { ref, listAll, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';

export const allowedImageExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

const matchesImageId = (fileName, id) =>
  allowedImageExtensions.some((ext) => fileName === `${String(id)}.${ext}`);

// Cache em memória para URLs já obtidas
const urlCache = new Map();

/**
 * Obtém a URL de download de uma imagem do Firebase Storage com cache.
 * @param {string} folder - Nome da pasta no storage (ex: 'patient', 'therapist').
 * @param {string|number} id - ID do arquivo.
 * @param {AbortSignal} [signal] - Sinal opcional para cancelar a requisição.
 * @returns {Promise<string>} - URL da imagem.
 */
export const getImageUrl = async (folder, id, signal) => {
  const cacheKey = `${folder}_${id}`;
  
  // Verificar cache em memória primeiro
  if (urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey);
  }

  // Se já foi abortado antes de começar, encerra cedo
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const folderRef = ref(storage, folder);

  try {
    const result = await listAll(folderRef);
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    const match = result.items.find((item) => matchesImageId(item.name, id));

    if (!match) return null;

    const url = await getDownloadURL(match);
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    
    // Salvar no cache
    urlCache.set(cacheKey, url);
    
    return url;
  } catch (error) {
    if (error.code === 'storage/object-not-found' || error.code === 'storage/unauthorized') {
      return null;
    }
    throw error;
  }
};

/**
 * Limpa o cache de URLs
 */
export const clearImageCache = () => {
  urlCache.clear();
};

/**
 * Remove uma URL específica do cache
 */
export const removeFromImageCache = (folder, id) => {
  const cacheKey = `${folder}_${id}`;
  urlCache.delete(cacheKey);
};

/**
 * Remove todas as imagens existentes de um ID (qualquer extensão permitida).
 */
export const deleteImagesById = async (folder, id) => {
  const folderRef = ref(storage, folder);
  const result = await listAll(folderRef);
  const toDelete = result.items.filter((item) => matchesImageId(item.name, id));

  await Promise.all(toDelete.map((item) => deleteObject(item)));
};
